/**
 * F 217 — the M2 attachment channel.
 *
 * WHY AN EXTENSION AND NOT A COMPONENT.
 *
 * Stage 1 of F 217 asked whether the editor can see its previous
 * sibling's kind. It can — `editor.document[i].type` — and that answer
 * is what this file acts on. But the OTHER half of the question is
 * harder, and the dispatch's description of M2 did not survive it:
 *
 *   M2 was described as "the editor renders each block". It does not.
 *   Block rendering lives inside BlockNote's BlockNoteView, and in
 *   0.47.3 it is not reachable from application code. Checked and
 *   recorded:
 *     - BlockNoteEditor.create() has no blockContainer/render hook.
 *     - ComponentsContext (the one override surface) exposes
 *       FormattingToolbar, FilePanel, LinkToolbar, SideMenu,
 *       SuggestionMenu, GridSuggestionMenu, TableHandle, Comments and
 *       Generic. There is no block renderer in that list.
 *
 * So the derivation is TypeScript, as M2 requires, and the DELIVERY is
 * a ProseMirror plugin supplied through BlockNote's Extension API
 * (`prosemirrorPlugins`, see BlockNoteExtension.d.ts). The decision
 * logic — which pairs get reduced — lives in `deriveSpacing`, in
 * TypeScript, and is unit-testable with no browser. That is the
 * substance of choosing M2 over M1: the lookup table is ours, not a
 * chain of brittle CSS selectors. Only the transport differs from the
 * dispatch's sketch.
 *
 * SHAPE, for the next agent.
 * `Extension` is an INTERFACE in 0.47.3, not an exported base class —
 * there is no `class X extends Extension` available, and the first
 * attempt at this file failed the build with exactly that error
 * ("Extension is not exported by @blocknote/core"). The base class is
 * minified internally and is not part of the public surface. A plain
 * object literal satisfying the interface is the supported shape:
 *     { key, prosemirrorPlugins }
 * wrapped by the exported `createExtension` factory.
 *
 * WHAT IT ATTACHES
 *   data-prev-kind  — the preceding sibling's BlockNote type
 *   data-kind       — this block's own type (for CSS and for debugging)
 *   data-fc-gap     — the derived topMargin in px
 * All three land on the blockContainer node, which BlockNote renders as
 * `.bn-block`. CSS is the surface for the VALUE; this supplies inputs.
 */

import { createExtension } from '@blocknote/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { deriveSpacing } from './blockSpacing';

export const neighbourSpacingKey = new PluginKey('fc-neighbour-spacing');

/** Walk the doc in order, remembering the previous block's kind, and
 *  decorate each blockContainer with its adjacency. */
function buildDecorations(doc: any): DecorationSet {
  const decos: Decoration[] = [];
  let prevKind: string | null = null;

  doc.descendants((node: any, pos: number) => {
    if (node.type?.name !== 'blockContainer') return;

    // A blockContainer's first child is its content node — the thing
    // that actually names the block. Verified against the 0.47 schema:
    // blockContainer > heading|paragraph|bulletListItem|codeBlock.
    const currentKind: string | null = node.firstChild?.type?.name ?? null;

    // Depth guard. A top-level blockContainer sits at depth 1:
    //     doc > blockGroup > blockContainer
    // which in a real document is pos 1, 6, 12, 18... — NOT a fixed
    // position. The first cut of this file tested `pos !== 1`, which
    // silently decorated only the first block in the document and
    // passed a glance because `data-fc-gap="3"` was visibly present.
    // Nested blocks (a list item containing a list) are deeper, and
    // their previous sibling is a different question than the one
    // Notion's rule answers; tightening those against this rule would
    // be an invention of exactly the kind Option A forbids.
    if (doc.resolve(pos).depth !== 1) return;

    const { topMargin } = deriveSpacing(prevKind, currentKind);

    const attrs: Record<string, string> = {
      'data-fc-gap': String(topMargin),
      'data-kind': currentKind ?? '',
    };
    if (prevKind) attrs['data-prev-kind'] = prevKind;

    decos.push(Decoration.node(pos, pos + node.nodeSize, attrs));
    prevKind = currentKind;
  });

  return DecorationSet.create(doc, decos);
}

export const neighbourSpacing = createExtension(() => ({
  key: 'neighbourSpacing' as const,
  prosemirrorPlugins: [
    new Plugin({
      key: neighbourSpacingKey,
      state: {
        init: (_config, state) => buildDecorations(state.doc),
        apply: (tr, old) => (tr.docChanged ? buildDecorations(tr.doc) : old),
      },
      props: {
        decorations: (state) => neighbourSpacingKey.getState(state),
      },
    }),
  ],
}));
